import { useCallback, useEffect, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import Modal from '../components/Modal';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api, apiUpload } from '../api/client';
import { date } from '../lib/format';

const LOGO_MAX_BYTES = 1024 * 1024; // 1 MB

const EMPTY = {
  name: '', company: '', tax_id: '', email: '', phone: '',
  line1: '', line2: '', city: '', state: '', postal_code: '',
  shipping_same: true,
  s_line1: '', s_line2: '', s_city: '', s_state: '', s_postal_code: '',
  logo: '', is_active: true,
};
const EMPTY_CONTACT = { name: '', title: '', email: '', phone: '', is_primary: false };

export default function Customers() {
  const { activeId } = useBusiness();
  const { can, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [contacts, setContacts] = useState([{ ...EMPTY_CONTACT, is_primary: true }]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [card, setCard] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [portalStatus, setPortalStatus] = useState(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [impBusy, setImpBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api(`/customers?business_id=${activeId}`)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => {
        setRows([]);
        if (e?.status && e.status !== 404) setError('Could not load customers. Check the API connection.');
      })
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api(`/businesses/${activeId}/stripe-status`).then((s) => setStripeEnabled(!!s.enabled)).catch(() => setStripeEnabled(false));
  }, [activeId]);

  // Returning from Stripe Checkout (card capture): confirm + reopen the customer.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const setup = p.get('stripe_setup');
    if (!setup) return;
    const custId = p.get('customer');
    const sessionId = p.get('session_id');
    window.history.replaceState({}, '', '/customers');
    if (setup === 'success' && custId && sessionId) {
      api(`/customers/${custId}/payment-method/confirm`, { method: 'POST', body: { session_id: sessionId } })
        .then(() => openEdit({ id: Number(custId) }))
        .catch(() => {});
    }
  }, []); // run once on mount

  function openNew() {
    setEditingId(null);
    setForm(EMPTY);
    setZipNote({ billing: null, shipping: null });
    lastZip.current = { billing: '', shipping: '' };
    setContacts([{ ...EMPTY_CONTACT, is_primary: true }]);
    setComments([]);
    setNewComment('');
    setFormError(null);
    setOpen(true);
  }

  // ---- CSV import ----
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState(null);

  function openImport() {
    setImportFile(null); setImportPreview(null); setImportResult(null); setImportError(null); setImportBusy(false);
    setImportOpen(true);
  }
  async function onImportFile(file) {
    setImportFile(file); setImportPreview(null); setImportResult(null); setImportError(null);
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('business_id', String(activeId));
      setImportPreview(await apiUpload('/customers/import', fd));
    } catch (e) { setImportError(e?.detail || 'Could not read that CSV.'); }
    finally { setImportBusy(false); }
  }
  async function doImport() {
    if (!importFile || !importPreview?.to_create) return;
    setImportBusy(true); setImportError(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile); fd.append('business_id', String(activeId)); fd.append('commit', 'true');
      const r = await apiUpload('/customers/import', fd);
      setImportResult(r); load();
    } catch (e) { setImportError(e?.detail || 'Import failed.'); }
    finally { setImportBusy(false); }
  }

  async function openEdit(row) {
    setEditingId(row.id);
    setFormError(null);
    setNewComment('');
    setZipNote({ billing: null, shipping: null });
    lastZip.current = { billing: '', shipping: '' };
    setOpen(true);
    try {
      const c = await api(`/customers/${row.id}`);
      const a = c.billing_address || {};
      const s = c.shipping_address || {};
      setForm({
        name: c.name || '', company: c.company || '', tax_id: c.tax_id || '',
        email: c.email || '', phone: c.phone || '',
        line1: a.line1 || '', line2: a.line2 || '', city: a.city || '', state: a.state || '', postal_code: a.postal_code || '',
        shipping_same: c.shipping_same !== false,
        s_line1: s.line1 || '', s_line2: s.line2 || '', s_city: s.city || '', s_state: s.state || '', s_postal_code: s.postal_code || '',
        logo: c.logo || '', is_active: c.is_active !== false,
      });
      setContacts(c.contacts?.length ? c.contacts : [{ ...EMPTY_CONTACT, is_primary: true }]);
      setComments(c.comments || []);
      setCard({ brand: c.card_brand, last4: c.card_last4, pm_type: c.pm_type, bank_name: c.bank_name, bank_last4: c.bank_last4, has_customer: !!c.stripe_customer_id });
      setPortalStatus(null); setInviteResult(null);
      api(`/portal-admin/status?customer_id=${row.id}`).then(setPortalStatus).catch(() => setPortalStatus({ status: 'none' }));
    } catch {
      setForm(EMPTY);
      setContacts([{ ...EMPTY_CONTACT, is_primary: true }]);
      setComments([]);
    }
  }

  async function addCard() {
    setCardBusy(true);
    setFormError(null);
    try {
      const { url } = await api(`/customers/${editingId}/payment-method/checkout`, {
        method: 'POST', body: { return_url: window.location.origin + '/customers' },
      });
      window.location.href = url; // redirect to Stripe-hosted card form
    } catch (e) { setFormError(e?.detail || 'Could not start card capture.'); setCardBusy(false); }
  }

  async function removeCard() {
    setCardBusy(true);
    setFormError(null);
    try {
      await api(`/customers/${editingId}/payment-method`, { method: 'DELETE' });
      setCard({ brand: null, last4: null, pm_type: null, bank_name: null, bank_last4: null, has_customer: true });
    } catch (e) { setFormError(e?.detail || 'Could not remove the card.'); }
    finally { setCardBusy(false); }
  }

  function openPortalModal() {
    setInviteResult(null); setLinkCopied(false);
    setInviteEmail((portalStatus && portalStatus.email) || form.email || '');
    setPortalOpen(true);
  }
  async function sendInvite() {
    if (!inviteEmail.trim()) return;
    setPortalBusy(true); setInviteResult(null);
    try {
      const r = await api('/portal-admin/invite', { method: 'POST', body: { customer_id: editingId, email: inviteEmail.trim() } });
      setInviteResult(r);
      api(`/portal-admin/status?customer_id=${editingId}`).then(setPortalStatus).catch(() => {});
    } catch (e) { setInviteResult({ error: e?.detail || 'Could not create the invite.' }); }
    finally { setPortalBusy(false); }
  }
  async function revokePortal() {
    setPortalBusy(true);
    try {
      await api('/portal-admin/revoke', { method: 'POST', body: { customer_id: editingId } });
      setPortalStatus({ status: 'disabled' }); setInviteResult(null);
    } catch { /* ignore */ }
    finally { setPortalBusy(false); }
  }
  // Open the portal in a new tab, logged in as this customer (read-only admin preview).
  async function accessAsCustomer() {
    setImpBusy(true); setInviteResult(null);
    const w = window.open('about:blank', '_blank'); // opened on the click so it isn't popup-blocked
    try {
      const r = await api('/portal-admin/impersonate', { method: 'POST', body: { customer_id: editingId } });
      if (w) w.location.href = r.url || '/portal'; else window.open(r.url || '/portal', '_blank', 'noopener');
    } catch (e) {
      if (w) w.close();
      setInviteResult({ error: e?.detail || 'Could not start the preview.' });
    } finally { setImpBusy(false); }
  }
  function copyLink() {
    if (inviteResult?.link) { navigator.clipboard?.writeText(inviteResult.link); setLinkCopied(true); }
  }
  const portalLine = !portalStatus ? 'Checking…'
    : portalStatus.status === 'active' ? `Active — ${portalStatus.email}${portalStatus.email_verified ? ' · verified' : ''}`
    : portalStatus.status === 'invited' ? `Invited — ${portalStatus.email} (awaiting setup)`
    : portalStatus.status === 'disabled' ? 'Access revoked'
    : 'No portal account yet';

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // ZIP -> city/state autofill. Fires once when a field reaches 5 digits.
  const lastZip = useRef({ billing: '', shipping: '' });
  const [zipNote, setZipNote] = useState({ billing: null, shipping: null });

  async function lookupZip(zip, target) {
    const cityKey = target === 'shipping' ? 's_city' : 'city';
    const stateKey = target === 'shipping' ? 's_state' : 'state';
    setZipNote((n) => ({ ...n, [target]: 'Looking up…' }));
    try {
      const r = await api(`/zip/${zip}`);
      setForm((f) => ({ ...f, [cityKey]: r.city, [stateKey]: r.state }));
      setZipNote((n) => ({ ...n, [target]: `${r.city}, ${r.state}` }));
    } catch (e) {
      setZipNote((n) => ({ ...n, [target]: e?.status === 404 ? 'ZIP not found' : 'Lookup unavailable — enter city/state manually' }));
    }
  }
  function onZip(value, target) {
    set(target === 'shipping' ? 's_postal_code' : 'postal_code', value);
    const clean = String(value || '').replace(/\D/g, '').slice(0, 5);
    if (clean.length === 5 && lastZip.current[target] !== clean) {
      lastZip.current[target] = clean;
      lookupZip(clean, target);
    } else if (clean.length < 5) {
      lastZip.current[target] = '';
      setZipNote((n) => ({ ...n, [target]: null }));
    }
  }

  // ---- contacts editing ----
  function setContact(i, field, value) {
    setContacts((cs) => cs.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));
  }
  function makePrimary(i) {
    setContacts((cs) => cs.map((c, idx) => ({ ...c, is_primary: idx === i })));
  }
  function addContact() {
    setContacts((cs) => [...cs, { ...EMPTY_CONTACT }]);
  }
  function removeContact(i) {
    setContacts((cs) => {
      const next = cs.filter((_, idx) => idx !== i);
      if (next.length && !next.some((c) => c.is_primary)) next[0].is_primary = true;
      return next.length ? next : [{ ...EMPTY_CONTACT, is_primary: true }];
    });
  }

  // ---- logo ----
  function onLogoFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > LOGO_MAX_BYTES) {
      setFormError('Logo must be under 1 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set('logo', reader.result);
    reader.readAsDataURL(file);
  }

  async function toggleArchive() {
    if (!editingId) return;
    const archived = form.is_active !== false; // currently active -> archive it
    if (archived && !window.confirm(`Archive ${form.name || 'this customer'}? They’ll be hidden from active lists but their history is kept.`)) return;
    setSaving(true); setFormError(null);
    try {
      const r = await api(`/customers/${editingId}/archive`, { method: 'POST', body: { archived } });
      setForm((f) => ({ ...f, is_active: r.is_active }));
      loadList();
    } catch (e) { setFormError(e?.detail || 'Could not update the customer.'); }
    finally { setSaving(false); }
  }

  async function save() {
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    setSaving(true);
    setFormError(null);
    const billing_address =
      form.line1 || form.line2 || form.city || form.state || form.postal_code
        ? { line1: form.line1, line2: form.line2, city: form.city, state: form.state, postal_code: form.postal_code }
        : null;
    const shipping_address = form.shipping_same
      ? null // resolved from billing when "same as billing"
      : form.s_line1 || form.s_line2 || form.s_city || form.s_state || form.s_postal_code
        ? { line1: form.s_line1, line2: form.s_line2, city: form.s_city, state: form.s_state, postal_code: form.s_postal_code }
        : null;
    const cleanContacts = contacts
      .filter((c) => c.name && c.name.trim())
      .map((c) => ({ name: c.name.trim(), title: c.title, email: c.email, phone: c.phone, is_primary: !!c.is_primary }));
    const body = {
      name: form.name.trim(), company: form.company || null, tax_id: form.tax_id || null,
      email: form.email || null, phone: form.phone || null, logo: form.logo || null,
      billing_address, shipping_address, shipping_same: form.shipping_same, contacts: cleanContacts,
    };
    try {
      if (editingId) {
        await api(`/customers/${editingId}`, { method: 'PUT', body: { ...body, is_active: form.is_active } });
      } else {
        await api('/customers', { method: 'POST', body: { ...body, business_id: activeId } });
      }
      setOpen(false);
      load();
    } catch (e) {
      setFormError(e?.detail || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function addComment() {
    const text = newComment.trim();
    if (!text || !editingId) return;
    try {
      const created = await api(`/customers/${editingId}/comments`, { method: 'POST', body: { body: text } });
      setComments((cs) => [created, ...cs]);
      setNewComment('');
    } catch (e) {
      setFormError(e?.detail || 'Could not add comment.');
    }
  }

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'company', header: 'Company', render: (r) => r.company || '—' },
    { key: 'primary_contact', header: 'Primary contact', render: (r) => r.primary_contact || '—' },
    { key: 'email', header: 'Email', render: (r) => r.email || '—' },
    { key: 'is_active', header: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'archived'} /> },
  ];

  return (
    <>
      <PageHeader
        title="Customers"
        actions={<div style={{ display: 'flex', gap: 8 }}>
          <a className="btn" href={`/api/customers/export?business_id=${activeId}`}>Export CSV</a>
          {can('customers.manage') && <button className="btn" onClick={openImport}>Import CSV</button>}
          {can('customers.manage') && <button className="btn btn--primary" onClick={openNew}>New customer</button>}
        </div>}
      />
      <section className="panel">
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          getRowKey={(r) => r.id}
          onRowClick={openEdit}
          empty="No customers yet. Add one to start billing."
        />
      </section>

      <Drawer
        open={open}
        title={editingId ? 'Edit customer' : 'New customer'}
        onClose={() => !saving && setOpen(false)}
        footer={
          <>
            <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            {editingId && user?.role === 'admin' && (
              <button className="btn btn--danger" onClick={toggleArchive} disabled={saving}>
                {form.is_active === false ? 'Restore' : 'Archive'}
              </button>
            )}
            <button className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create customer'}
            </button>
          </>
        }
      >
        {formError && <div className="form-error">{formError}</div>}

        {/* Company */}
        <h3 className="form-section">Company</h3>
        <div className="logo-row">
          <div className="logo-box">
            {form.logo
              ? <img src={form.logo} alt="logo" />
              : <span className="logo-box__empty">No logo</span>}
          </div>
          <div className="logo-actions">
            <label className="btn btn--sm">
              {form.logo ? 'Replace logo' : 'Upload logo'}
              <input type="file" accept="image/*" onChange={onLogoFile} hidden />
            </label>
            {form.logo && (
              <button type="button" className="btn btn--sm" onClick={() => set('logo', '')}>Remove</button>
            )}
            <span className="hint">PNG/JPG, under 1 MB</span>
          </div>
        </div>
        <div className="field">
          <label>Name *</label>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Company</label>
            <input value={form.company} onChange={(e) => set('company', e.target.value)} />
          </div>
          <div className="field">
            <label>Tax ID</label>
            <input value={form.tax_id} onChange={(e) => set('tax_id', e.target.value)} />
          </div>
          <div className="field">
            <label>Billing email</label>
            <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div className="field">
            <label>Phone</label>
            <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </div>
        </div>
        {/* Billing address */}
        <h3 className="form-section">Billing address</h3>
        <div className="field">
          <label>Street</label>
          <input value={form.line1} onChange={(e) => set('line1', e.target.value)} />
        </div>
        <div className="field">
          <label>Suite / Apt / Unit</label>
          <input value={form.line2} onChange={(e) => set('line2', e.target.value)} />
        </div>
        <div className="form-grid form-grid--3">
          <div className="field"><label>City</label>
            <input value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
          <div className="field"><label>State</label>
            <input value={form.state} onChange={(e) => set('state', e.target.value)} /></div>
          <div className="field"><label>ZIP</label>
            <input value={form.postal_code} inputMode="numeric" onChange={(e) => onZip(e.target.value, 'billing')} /></div>
        </div>
        {zipNote.billing && <p className="hint hint--zip">{zipNote.billing}</p>}

        {/* Shipping address */}
        <h3 className="form-section">Shipping address</h3>
        <label className="check">
          <input
            type="checkbox"
            checked={form.shipping_same}
            onChange={(e) => set('shipping_same', e.target.checked)}
          />
          Same as billing address
        </label>
        {!form.shipping_same && (
          <div className="ship-fields">
            <div className="field">
              <label>Street</label>
              <input value={form.s_line1} onChange={(e) => set('s_line1', e.target.value)} />
            </div>
            <div className="field">
              <label>Suite / Apt / Unit</label>
              <input value={form.s_line2} onChange={(e) => set('s_line2', e.target.value)} />
            </div>
            <div className="form-grid form-grid--3">
              <div className="field"><label>City</label>
                <input value={form.s_city} onChange={(e) => set('s_city', e.target.value)} /></div>
              <div className="field"><label>State</label>
                <input value={form.s_state} onChange={(e) => set('s_state', e.target.value)} /></div>
              <div className="field"><label>ZIP</label>
                <input value={form.s_postal_code} inputMode="numeric" onChange={(e) => onZip(e.target.value, 'shipping')} /></div>
            </div>
            {zipNote.shipping && <p className="hint hint--zip">{zipNote.shipping}</p>}
          </div>
        )}

        {/* Contacts */}
        <h3 className="form-section">Contacts</h3>
        {contacts.map((c, i) => (
          <div className="contact-card" key={i}>
            <div className="contact-card__top">
              <label className="radio">
                <input type="radio" name="primary" checked={!!c.is_primary} onChange={() => makePrimary(i)} />
                Primary
              </label>
              <button type="button" className="link-danger" onClick={() => removeContact(i)}>Remove</button>
            </div>
            <div className="form-grid">
              <div className="field"><label>Name</label>
                <input value={c.name} onChange={(e) => setContact(i, 'name', e.target.value)} /></div>
              <div className="field"><label>Title</label>
                <input value={c.title} onChange={(e) => setContact(i, 'title', e.target.value)} /></div>
              <div className="field"><label>Email</label>
                <input type="email" value={c.email} onChange={(e) => setContact(i, 'email', e.target.value)} /></div>
              <div className="field"><label>Phone</label>
                <input value={c.phone} onChange={(e) => setContact(i, 'phone', e.target.value)} /></div>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn--sm" onClick={addContact}>+ Add contact</button>

        {/* Payment method — only for existing customers when Stripe is enabled */}
        {editingId && stripeEnabled && (
          <>
            <h3 className="form-section">Payment method</h3>
            {card && (card.last4 || card.bank_last4) ? (
              <div className="card-onfile">
                {card.pm_type === 'us_bank_account' ? (
                  <>
                    <span className="card-onfile__brand">{(card.bank_name || 'Bank')} · ACH</span>
                    <span className="card-onfile__num">•••• {card.bank_last4}</span>
                  </>
                ) : (
                  <>
                    <span className="card-onfile__brand">{(card.brand || 'card').toUpperCase()}</span>
                    <span className="card-onfile__num">•••• {card.last4}</span>
                  </>
                )}
                <button type="button" className="btn btn--sm btn--danger" onClick={removeCard} disabled={cardBusy}>Remove</button>
              </div>
            ) : (
              <div className="card-onfile card-onfile--empty">
                <span className="hint">No payment method on file. Add a card or bank account (ACH) to enable auto-charge on contracts.</span>
                <button type="button" className="btn btn--sm" onClick={addCard} disabled={cardBusy}>{cardBusy ? 'Opening…' : 'Add card or bank (ACH)'}</button>
              </div>
            )}
          </>
        )}

        {/* Portal access — only for existing customers */}
        {editingId && (
          <>
            <h3 className="form-section">Portal access</h3>
            <div className="portal-access">
              <span className="hint">{portalLine}</span>
              {can('customers.manage') && (
                <button type="button" className="btn btn--sm" onClick={openPortalModal}>
                  {portalStatus && portalStatus.status === 'active' ? 'Manage / re-invite' : 'Invite to portal'}
                </button>
              )}
              {user?.role === 'admin' && (
                <button type="button" className="btn btn--sm" onClick={accessAsCustomer} disabled={impBusy} title="Open the portal as this customer (read-only preview)">
                  {impBusy ? 'Opening…' : 'Access as customer'}
                </button>
              )}
            </div>
          </>
        )}

        {/* Comments — only for existing customers */}
        {editingId && (
          <>
            <h3 className="form-section">Comments</h3>
            <div className="comment-add">
              <textarea
                rows={2}
                placeholder="Add a comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <button type="button" className="btn btn--sm btn--primary" onClick={addComment} disabled={!newComment.trim()}>
                Add
              </button>
            </div>
            <div className="comment-log">
              {comments.length === 0 && <div className="comment-empty">No comments yet.</div>}
              {comments.map((cm) => (
                <div className="comment" key={cm.id}>
                  <div className="comment__meta">
                    <span className="comment__author">{cm.author || 'Unknown'}</span>
                    <span className="comment__time">{date(cm.created_at)} · {new Date(cm.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                  <div className="comment__body">{cm.body}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </Drawer>

      <Modal
        open={portalOpen}
        title="Customer portal access"
        onClose={() => setPortalOpen(false)}
        footer={
          <>
            <button className="btn" onClick={() => setPortalOpen(false)}>Close</button>
            {portalStatus && (portalStatus.status === 'active' || portalStatus.status === 'invited') && (
              <button className="btn btn--danger" onClick={revokePortal} disabled={portalBusy}>Revoke access</button>
            )}
            <button className="btn btn--primary" onClick={sendInvite} disabled={portalBusy || !inviteEmail.trim()}>
              {portalBusy ? 'Sending…' : (portalStatus && portalStatus.status !== 'none' ? 'Re-send invite' : 'Send invite')}
            </button>
          </>
        }
      >
        <p className="hint" style={{ marginTop: 0 }}>
          Invite this customer to the self-service portal to view their invoices, quotes, and payments.
          They confirm their email and set a password through the invite link.
        </p>
        <div className="field"><label>Invite email</label>
          <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="name@company.com" /></div>

        {inviteResult?.error && <div className="form-error">{inviteResult.error}</div>}
        {inviteResult?.link && (
          <div className="invite-result">
            <p className="hint">
              {inviteResult.emailed
                ? `Invite emailed to ${inviteResult.email}. You can also share the link directly:`
                : 'Email sending isn’t configured — copy this link and send it to the customer:'}
            </p>
            <div className="invite-link">
              <input readOnly value={inviteResult.link} onFocus={(e) => e.target.select()} />
              <button type="button" className="btn btn--sm" onClick={copyLink}>{linkCopied ? 'Copied' : 'Copy'}</button>
            </div>
          </div>
        )}
      </Modal>

      <Drawer open={importOpen} title="Import customers from CSV" width={620} onClose={() => setImportOpen(false)}>
        {importResult ? (
          <div>
            <div className="form-note">Imported <strong>{importResult.created}</strong> customer{importResult.created === 1 ? '' : 's'}.</div>
            <p className="hint">{importResult.duplicates} duplicate{importResult.duplicates === 1 ? '' : 's'} skipped · {importResult.errors} error{importResult.errors === 1 ? '' : 's'}.</p>
            <div style={{ marginTop: 16 }}><button className="btn btn--primary" onClick={() => setImportOpen(false)}>Done</button></div>
          </div>
        ) : (
          <>
            <p className="hint" style={{ marginTop: 0 }}>
              Upload a CSV. Recognized headers include Name/Display Name, Company, Email, Phone, and address
              columns (Line1/Billing Address, City, State, Postal Code/Billing Code) — a <strong>Zoho Books
              contacts export works as-is</strong>. Rows already in Flightdeck (same name or email) are skipped.
              <br /><a href={`/api/customers/export?business_id=${activeId}`}>Download the current list</a> to use as a template.
            </p>

            <div className="field">
              <label>CSV file</label>
              <input type="file" accept=".csv,text/csv" onChange={(e) => onImportFile(e.target.files?.[0] || null)} />
            </div>

            {importBusy && !importPreview && <p className="hint">Reading…</p>}
            {importError && <div className="form-error">{importError}</div>}

            {importPreview && (
              <>
                <div className="totals" style={{ marginTop: 12 }}>
                  <div className="totals__row"><span>Rows in file</span><span className="num">{importPreview.total}</span></div>
                  <div className="totals__row totals__row--grand"><span>New to import</span><span className="num">{importPreview.to_create}</span></div>
                  <div className="totals__row"><span>Duplicates skipped</span><span className="num">{importPreview.duplicates}</span></div>
                  <div className="totals__row"><span>Errors</span><span className="num">{importPreview.errors}</span></div>
                </div>

                {importPreview.unmapped?.length > 0 && (
                  <p className="hint">Ignored columns: {importPreview.unmapped.join(', ')}</p>
                )}
                {importPreview.error_detail?.length > 0 && (
                  <p className="form-error">{importPreview.error_detail.map((e) => `line ${e.line}: ${e.reason}`).join(' · ')}</p>
                )}

                {importPreview.sample?.length > 0 && (
                  <>
                    <h3 className="form-section">Preview (first {importPreview.sample.length})</h3>
                    <table className="mini-table">
                      <thead><tr><th>Name</th><th>Email</th><th>City</th></tr></thead>
                      <tbody>
                        {importPreview.sample.map((c, i) => (
                          <tr key={i}><td>{c.name}</td><td>{c.email || '—'}</td><td>{c.billing_address?.city || '—'}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <button className="btn btn--primary" onClick={doImport} disabled={importBusy || !importPreview.to_create}>
                    {importBusy ? 'Importing…' : `Import ${importPreview.to_create} customer${importPreview.to_create === 1 ? '' : 's'}`}
                  </button>
                  <button className="btn" onClick={() => setImportOpen(false)}>Cancel</button>
                </div>
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
